import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ChevronDown, Building2 } from "lucide-react";
import { useLang } from "@/i18n";

const HERO_IMG = "/assets/hero-car.jpg";
const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={HERO_IMG}
          alt="Renault Arkana arancione al tramonto — Taxi Bellinzona"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070709]/75 via-[#070709]/55 to-[#070709]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070709]/90 via-[#070709]/45 to-[#070709]/20" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-5 pt-28 sm:px-8"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mb-8 inline-flex items-center gap-3 rounded-full border border-amber-400/30 bg-[#070709]/60 px-4 py-2 backdrop-blur-sm"
          data-testid="hero-live-badge"
        >
          <span className="live-dot h-2 w-2 rounded-full bg-green-500" />
          <span className="font-mono-alt text-[11px] uppercase tracking-[0.25em] text-amber-300">
            {t.hero.badge}
          </span>
        </motion.div>

        <h1 className="max-w-3xl font-display font-extrabold tracking-tight">
          <MaskedLine delay={0.3} className="text-4xl leading-[1.05] text-white sm:text-5xl lg:text-6xl">
            {t.hero.line3}
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6, ease: EASE }}
          className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
          data-testid="hero-subtitle"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <a
            href="tel:+41766344242"
            data-testid="hero-call-button-primary"
            className="gold-glow group inline-flex items-center justify-center gap-3 rounded-full bg-amber-400 px-8 py-4 font-display text-base font-extrabold uppercase tracking-wide text-black transition-all duration-300 hover:scale-[1.03] hover:bg-amber-300"
          >
            <Phone className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />
            +41 76 634 4242
            <span className="font-mono-alt text-[10px] font-semibold uppercase tracking-widest opacity-70">
              {t.hero.h24}
            </span>
          </a>
          <a
            href="tel:+41912292464"
            data-testid="hero-call-button-secondary"
            className="group inline-flex items-center justify-center gap-3 rounded-full border border-white/20 bg-[#070709]/50 px-8 py-4 font-display text-base font-bold text-white backdrop-blur-sm transition-all duration-300 hover:border-amber-400/60 hover:text-amber-300"
          >
            <Building2 className="h-5 w-5" />
            {t.hero.ctaSecondary}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.05 }}
          className="mt-14 flex items-center gap-6 font-mono-alt text-[11px] uppercase tracking-[0.25em] text-slate-400"
        >
          <span>{t.hero.note1}</span>
          <span className="h-px w-10 bg-amber-400/50" />
          <span>{t.hero.note2}</span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        >
          <ChevronDown className="h-6 w-6 text-amber-400/70" />
        </motion.div>
      </motion.div>
    </section>
  );
};
